import React, { useEffect, useState, useCallback } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import PartySelectSection from '../../../../documents/components/PartySelectSection'
import { request } from '../../../../../../utils/request';
import { useForm } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { Box, ToggleButton, ToggleButtonGroup, Card, Typography, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import InvoiceDatesSection from '../../../../documents/components/InvoiceDatesSection';
import NotesTermsSection from '../../../../documents/components/NotesTermsSection';
import AmountSummarySection from '../../../../documents/components/AmountSummarySection';
import ItemsTableSection from '../../../../documents/components/ItemsTableSection';
import { CreditNoteFormData, GstType, TaxBreakdownItem } from '../../../../types';
import { useGstMode } from '../../../../../../utils/useGstMode';
import { toast } from 'react-toastify';
import { useFinancialYear } from '../../../../../../utils/useFinancialYear';
import { useSearchParams } from 'react-router-dom';

const CreateCreditNote = () => {
  const [searchParams] = useSearchParams();
  const financialYear = useFinancialYear();
  const defaultCreditNoteNumber = `CN${financialYear}-1`
  const navigate = useNavigate();
  const { gstEnabled } = useGstMode();
  const { id } = useParams();
  const isEditMode = !!id;

  const [taxableAmount, setTaxableAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [, setTotalTax] = useState(0);
  const [, setTotalDiscount] = useState(0);
  const [, setItems] = useState<any[]>([]);
  const [taxBreakdown, setTaxBreakdown] = useState<TaxBreakdownItem[]>([]);
  const [nextCreditNoteNumber, setNextCreditNoteNumber] = useState(defaultCreditNoteNumber);
  const [isLoadingCreditNoteNumber, setIsLoadingCreditNoteNumber] = useState(false);
  const [loading, setLoading] = useState(false);

  const queryGstType = searchParams.get('gstType');
  const typeName = 'CREDIT_NOTE'

  // Determine default GST type based on query parameter or gstEnabled
  const getDefaultGstType = (): GstType => {
    if (queryGstType === 'NON-GST') {
      return 'NON-GST';
    }
    return gstEnabled ? 'GST' : 'GST';
  };

  const methods = useForm<CreditNoteFormData>({
    defaultValues: {
      creditNoteType: typeName,
      gstType: getDefaultGstType(), // Use the function to get default
      party: '',
      paymentType: 'CASH',
      invoiceId: '',
      creditNoteNumber: '',
      creditNoteDate: new Date().toISOString().split('T')[0],
      date: new Date().toISOString().split('T')[0],
      items: [],
      charges: [],
      discount: undefined,
      notes: '',
      terms: '',
      roundOff: false,
      totalAmount: 0,
      taxableAmount: 0,
      subtotal: 0,
      receivedAmount: 0,
      balanceAmount: 0,
      taxBreakdown: [],
    },
    mode: 'onChange'
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    getValues,
    reset
  } = methods;

  // Watch gstType value
  const gstType = watch('gstType', getDefaultGstType());
  const creditNoteType = watch('creditNoteType', typeName);
  const selectedPartyId = watch('party');
  const selectedInvoiceId = watch('invoiceId');

  // Function to fetch next credit note number from API
  const fetchNextCreditNoteNumber = useCallback(async () => {
    if (isEditMode) return; // Don't fetch for edit mode

    setIsLoadingCreditNoteNumber(true);
    try {
      const res = await request({
        url: 'creditNote/get-next-credit-note-number',
        method: "get",
        params: { creditNoteType: creditNoteType, gstType: gstType }
      });

      if (res.success && res.creditNoteNumber) {
        const newCreditNoteNumber = res.creditNoteNumber;
        setNextCreditNoteNumber(newCreditNoteNumber);
        setValue('creditNoteNumber', newCreditNoteNumber);
      }
    } catch (error) {
      console.error("Error fetching next credit note number:", error);
      // Fallback: Use default
      setNextCreditNoteNumber(defaultCreditNoteNumber);
      setValue('creditNoteNumber', defaultCreditNoteNumber);
    } finally {
      setIsLoadingCreditNoteNumber(false);
    }
  }, [gstType, creditNoteType, isEditMode, setValue, defaultCreditNoteNumber]);

  // Function to fetch existing credit note data for edit mode
  const fetchCreditNoteData = useCallback(async () => {
    if (!isEditMode) return;

    setLoading(true);
    try {
      const res = await request({
        url: `creditNote/get-single-credit-note/${id}`,
        method: "get"
      });

      if (res.success && res.data) {
        const creditNoteData = res.data;

        // Format dates to YYYY-MM-DD
        const formatDate = (dateString: string | Date) => {
          if (!dateString) return new Date().toISOString().split('T')[0];
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };

        // Get party ID - handle both object and string
        let partyId = '';
        if (typeof creditNoteData.party === 'object' && creditNoteData.party !== null) {
          partyId = creditNoteData.party._id || '';
        } else if (typeof creditNoteData.party === 'string') {
          partyId = creditNoteData.party;
        }

        // Prepare form data
        const formData = {
          creditNoteType: creditNoteData.creditNoteType || typeName,
          gstType: creditNoteData.gstType || 'GST',
          paymentType: creditNoteData.paymentType,
          party: partyId,
          invoiceId: creditNoteData.invoiceId || undefined,
          creditNoteNumber: creditNoteData.creditNoteNumber || '',
          creditNoteDate: formatDate(creditNoteData.creditNoteDate),
          date: formatDate(creditNoteData.date),
          dueDate: formatDate(creditNoteData.dueDate),
          paymentTerms: creditNoteData.paymentTerms || '',
          items: creditNoteData.items || [],
          charges: creditNoteData.charges || [],
          discount: creditNoteData.discount || undefined,
          notes: creditNoteData.notes || '',
          terms: creditNoteData.terms || '',
          roundOff: creditNoteData.roundOff || false,
          totalAmount: creditNoteData.totalAmount || 0,
          taxableAmount: creditNoteData.taxableAmount || 0,
          subtotal: creditNoteData.subtotal || 0,
          receivedAmount: creditNoteData.receivedAmount || 0,
          balanceAmount: creditNoteData.balanceAmount || 0,
          taxBreakdown: creditNoteData.taxBreakdown || [],
        };

        // Reset form with fetched data
        reset(formData);

        // Update state values
        setTaxableAmount(creditNoteData.taxableAmount || 0);
        setTotalAmount(creditNoteData.totalAmount || 0);
        setTotalTax(0); // You might need to calculate this from items
        setTotalDiscount(0); // You might need to calculate this from items
        setItems(creditNoteData.items || []);
        setTaxBreakdown(creditNoteData.taxBreakdown || []);

      } else {
        toast.error(res.message || "Failed to load credit note data");
      }
    } catch (error: any) {
      console.error("Error fetching credit note data:", error);
      toast.error(error?.message || "Failed to load credit note data");
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode, reset]);

  // Function to update all items tax to "none" when NON-GST is selected
  const updateItemsTaxToNone = useCallback(() => {
    const currentItems = getValues('items') || [];
    if (currentItems.length === 0) return;

    const updatedItems = currentItems.map((item: any) => ({
      ...item,
      tax: {
        type: 'none',
        rate: 0
      },
      amount: ((item.quantity || 1) * (item.price || 0)) - (item.discount?.amount || 0)
    }));

    setItems(updatedItems);
    setValue('items', updatedItems);
    setTaxBreakdown([]);
    setValue('taxBreakdown', []);
  }, [getValues, setValue]);

  // Effect to handle query parameter for GST type (only for create mode)
  useEffect(() => {
    if (!isEditMode && queryGstType === 'NON-GST') {
      setValue('gstType', 'NON-GST');
      // Update items tax to none for NON-GST
      updateItemsTaxToNone();
    }
  }, [isEditMode, queryGstType, setValue, updateItemsTaxToNone]);

  // Initial data fetch
  useEffect(() => {
    if (isEditMode) {
      fetchCreditNoteData();
    } else {
      fetchNextCreditNoteNumber();
    }
  }, [isEditMode, fetchCreditNoteData, fetchNextCreditNoteNumber]);

  // Update next credit note number when credit note type changes (only for create mode)
  useEffect(() => {
    if (!isEditMode) {
      fetchNextCreditNoteNumber();
    }
  }, [creditNoteType, isEditMode, fetchNextCreditNoteNumber]);

  const requestWrapper = ({ url, method, params }: { url: string; method: string; params?: any }) => {
    return request({ method: method as any, url, params });
  }

  // Handle GST type change
  const handleGstTypeChange = (event: React.MouseEvent<HTMLElement>, newGstType: GstType | null) => {
    if (newGstType !== null) {
      setValue('gstType', newGstType);
      if (newGstType === 'NON-GST') {
        updateItemsTaxToNone();
      }
    }
  };

  // Effect to handle GST type changes
  useEffect(() => {
    if (gstType === 'NON-GST') {
      updateItemsTaxToNone();
    }
  }, [gstType, updateItemsTaxToNone]);

  // Add this useEffect right after your other useEffect hooks
  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      if (!selectedInvoiceId) {
        // Clear the form if no invoice is selected
        setValue('items', []);
        setTaxableAmount(0);
        setTotalAmount(0);
        setReceivedAmount(0);
        setTaxBreakdown([]);
        return;
      }

      try {
        const res = await request({
          url: `invoice/get-single-sales-invoice/${selectedInvoiceId}`,
          method: 'get'
        });

        if (res.success && res.data) {
          const invoiceData = res.data;

          // Auto-fill items from the selected invoice
          if (invoiceData.items && invoiceData.items.length > 0) {
            // Transform invoice items to credit note items
            const itemsToAdd = invoiceData.items.map((item: any, index: any) => ({
              id: item.id,
              srNo: index + 1,
              productId: item.productId,
              itemName: item.product?.itemName || item.itemName || '',
              description: item.description || '',
              quantity: item.quantity || 1,
              price: item.price || 0,
              amount: item.amount || 0,
              discount: item.discount || { type: 'none', amount: 0 },
              tax: item.tax || { type: 'none', rate: 0 },
              hsnNo: item.hsnNo || '',
            }));

            // Update items in the form
            setItems(itemsToAdd);
            setValue('items', itemsToAdd);

            // Update taxable amount if GST invoice
            if (invoiceData.taxableAmount) {
              setTaxableAmount(invoiceData.taxableAmount);
              setValue('taxableAmount', invoiceData.taxableAmount);
            }

            // Update total amount
            if (invoiceData.totalAmount) {
              setTotalAmount(invoiceData.totalAmount);
              setValue('totalAmount', invoiceData.totalAmount);
            }

            // Update received amount
            // if (invoiceData.receivedAmount) {
            //   setReceivedAmount(invoiceData.totalAmount);
            //   setValue('receivedAmount', invoiceData.totalAmount);
            // }

            // Update charges
            if (invoiceData.charges) {
              // setReceivedAmount(invoiceData.receivedAmount);
              setValue('charges', invoiceData.charges);
            }

            // Update discount
            if (invoiceData.discount) {
              // setReceivedAmount(invoiceData.receivedAmount);
              setValue('discount', invoiceData.discount);
            }

            if (invoiceData?.paymentType) {
              setValue('paymentType', invoiceData.paymentType);
            }

            // Update tax breakdown if GST
            if (invoiceData.taxBreakdown && gstType === 'GST') {
              setTaxBreakdown(invoiceData.taxBreakdown);
              setValue('taxBreakdown', invoiceData.taxBreakdown);
            }
          } else {
            console.log("❌ No items found in invoice");
          }

          // Auto-fill notes with reference to the invoice
          if (invoiceData.notes) {
            const currentNotes = getValues('notes') || '';
            const invoiceReference = `Credit Note created against Invoice: ${invoiceData.invoiceNumber}\n${invoiceData.notes}`;

            if (!currentNotes.includes(invoiceData.notes)) {
              setValue('notes', `${currentNotes ? currentNotes + '\n\n' : ''}${invoiceReference}`);
            }
          }
          toast.success(`Invoice ${invoiceData.invoiceNumber} details loaded into credit note`);
        } else {
          toast.error('Failed to load invoice details');
        }
      } catch (error) {
        toast.error('Failed to load invoice details');
      }
    };

    // Only fetch if we're in create mode (not edit mode)
    if (!isEditMode) {
      fetchInvoiceDetails();
    }
  }, [selectedInvoiceId, setValue, getValues, gstType, isEditMode, setItems, setTaxableAmount, setTotalAmount, setTaxBreakdown]);

  const onSubmit = async (data: CreditNoteFormData) => {

    try {
      // Prepare the payload
      const payload = {
        ...data,
        // Make sure party is sent as ID only
        party: data.party,
        invoiceId: data.invoiceId || undefined,
        // Send the credit note number for edit mode
        creditNoteNumber: isEditMode ? data.creditNoteNumber : (data.creditNoteNumber || undefined),
        receivedAmount : data.totalAmount
      };

      const apiUrl = isEditMode
        ? `creditNote/update-credit-note/${id}`
        : 'creditNote/create-credit-note';
      const method = isEditMode ? 'put' : 'post';

      const res = await request({
        url: apiUrl,
        method: method,
        data: payload
      });

      if (res.success) {
        const successMessage = isEditMode
          ? "Credit Note Updated Successfully"
          : "Credit Note Created Successfully";
        toast.success(res.message || successMessage);

        // Navigate to credit note list
        navigate('/accounts/sales/creditNote');
      } else {
        toast.error(res.message || "Operation failed");
      }
    } catch (error: any) {
      console.error("Error submitting credit note:", error);
      toast.error(error || 'Something went wrong');
    }
  }

  const handleCancel = () => {
    navigate('/accounts/sales/creditNote');
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography>Loading credit note data...</Typography>
      </Box>
    );
  }

  return (
    <>
      <DocumentHeader
        heading={isEditMode ? 'Edit Credit Note' : 'Create Credit Note'}
        showBackButton
        showCancelButton={true}
        showSaveButton={true}
        onCancel={handleCancel}
        onSave={handleSubmit(onSubmit)}
        saveButtonText={isEditMode ? 'Update Credit Note' : 'Create Credit Note'}
        saveDisabled={isSubmitting}
      />

      {/* GST Type Selection Section */}
      {!gstEnabled && (
        <Card sx={{
          mb: 2,
          p: 2,
          borderRadius: 1,
          boxShadow: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" fontWeight="medium">
              Credit Note Type:
            </Typography>

            <ToggleButtonGroup
              value={gstType}
              exclusive
              onChange={handleGstTypeChange}
              aria-label="GST Type"
              size="small"
              color="primary"
              disabled={isEditMode} // Disable in edit mode
            >
              <ToggleButton value="GST" aria-label="GST">
                GST CN
              </ToggleButton>
              <ToggleButton value="NON-GST" aria-label="NON-GST">
                Non-GST CN
              </ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="caption" color={gstType === 'GST' ? "success.main" : "info.main"}>
              {gstType === 'GST'
                ? 'Includes GST tax calculations'
                : 'No GST tax calculations'}
            </Typography>
          </Box>
          {isEditMode && (
            <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
              GST type cannot be changed for existing credit notes.
            </Alert>
          )}
        </Card>
      )}

      {/* Form Provider */}
      <FormProvider {...methods}>
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={'flex'} gap={1} sx={{ alignItems: 'stretch' }}>
            <Box sx={{ flex: 1, height: 'auto' }}>
              <PartySelectSection
                request={requestWrapper}
                apiParams={{ status: 'active', limit: 50 }}
                billToLabel="Bill To"
                formName="party"
                onAddNewParty={() => navigate('/parties/addParty')}
                compact={true}
                sendPartyIdOnly={true}
              // Pass the current party value for edit mode
              />

              {errors.party && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Please select a party
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <InvoiceDatesSection
                invoiceNumberLabel="Credit Note No"
                invoiceDateLabel="Credit Note Date"
                formFieldNames={{
                  invoiceNumber: 'creditNoteNumber',
                  invoiceDate: 'creditNoteDate',
                  invoiceId: 'invoiceId'
                }}
                partyId={selectedPartyId}
                gstType={gstType}
                autoGenerateInvoiceNumber={!isEditMode}
                nextInvoiceNumber={nextCreditNoteNumber}
                showPaymentTerms={false}
                showDueDate={false}
                compact={true}
                isInvoiceLink={true}
                disabled={isLoadingCreditNoteNumber || isEditMode} // Disable invoice number in edit mode
                onInvoiceDateChange={() => { }}
                onPaymentTermsChange={() => { }}
                onDueDateChange={() => { }}
                paymentTermsOptions={[]}
              />
              {isLoadingCreditNoteNumber && !isEditMode && (
                <Typography variant="caption" color="text.secondary">
                  Loading next credit note number...
                </Typography>
              )}
              {nextCreditNoteNumber && !isEditMode && (
                <Typography variant="caption" color="primary">
                  Next available number: {nextCreditNoteNumber}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Items Table Section */}
          <Box>
            <ItemsTableSection
              formFieldName="items"
              onItemsChange={(updatedItems) => {
                setItems(updatedItems);
                setValue('items', updatedItems);
              }}
              onSubtotalChange={() => { }}
              onTotalDiscountChange={(discount) => {
                setTotalDiscount(discount);
              }}
              onTotalTaxChange={(tax) => {
                setTotalTax(tax);
              }}
              onTaxableAmountChange={(taxable) => {
                setTaxableAmount(taxable);
                setValue('taxableAmount', taxable);
              }}
              onTotalAmountChange={(total) => {
                setTotalAmount(total);
                setValue('totalAmount', total);
              }}
              onTaxBreakdownChange={(breakdown) => {
                if (gstType === 'GST') {
                  setTaxBreakdown(breakdown);
                  setValue('taxBreakdown', breakdown);
                }
              }}
              showHsnNo={gstType === 'GST'}
              showDiscount={true}
              showTax={gstType === 'GST'}
              disabled={false}
              compact={true}
              maxItems={100}
            // Items will be automatically populated from the form
            />
          </Box>

          <Box display={'flex'} gap={1} sx={{ alignItems: "stretch" }}>
            <Box sx={{ flex: 1 }}>
              <NotesTermsSection
                notesLabel="Additional Notes"
                termsLabel="Terms & Conditions"
                addNoteButtonText="Add Note"
                addTermsButtonText="Add Terms"
                formFieldNames={{
                  notes: 'notes',
                  terms: 'terms'
                }}
                showNotes={true}
                showTerms={true}
                allowMultipleNotes={false}
                allowMultipleTerms={false}
                compact={true}
                maxNotesLength={500}
                maxTermsLength={1000}
                onNotesChange={() => { }}
                onTermsChange={() => { }}
                notesPlaceholder="Add any special instructions, delivery notes, or additional information..."
                termsPlaceholder="Add payment terms, return policies, warranties, or other conditions..."
                defaultExpanded={!!getValues('notes') || !!getValues('terms')}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <AmountSummarySection
                taxableAmount={taxableAmount}
                totalAmount={totalAmount}
                taxBreakdown={gstType === 'GST' ? taxBreakdown : []}
                receivedAmount={receivedAmount}
                receivedAmountLabel='Paid Amount'
                formFieldNames={{
                  charges: 'charges',
                  discount: 'discount',
                  roundOff: 'roundOff',
                  totalAmount: 'totalAmount',
                  taxableAmount: 'taxableAmount',
                  subtotal: 'subtotal',
                  paymentType: 'paymentType',
                  receivedAmount: 'receivedAmount',
                  taxBreakdown: 'taxBreakdown',
                  balanceAmount: 'balanceAmount'
                }}
                compact={true}
                disabled={true}
                taxRate={gstType === 'GST' ? 18 : 0}
                showTaxableAmount={gstType === 'GST'}
                onTotalAmountChange={() => { }}
              />
            </Box>
          </Box>
        </Box>
      </FormProvider>
    </>
  )
}

export default CreateCreditNote;